import { logger } from "@jslib/shared/utils/logger";

import Message from "@components/Message";

import { getOption } from "../options/options";
import { speakToOuterFrame } from "../lib/frames/frameMessages";
import { useVerificationResponse } from "../lib/state/contextHooks";

import { TryAgainButtonComponent } from "./TryAgainButtonComponent";
import { LinkExternal } from "./LinkExternal";

export const TryAgainActionType = {
  NONE: "none",
  RESET: "reset",
  POSTMESSAGE: "postMessage",
  REDIRECT: "redirect",
} as const;

const PostMessageButton = () => {
  // @TODO - Add debounce to prevent double click triggering the postMesssage twice
  const verificationResponse = useVerificationResponse();

  const onClickHandler = () =>
    speakToOuterFrame({
      action: "tryAgain",
      verificationResponse,
    });

  return (
    <button
      type="button"
      className="sid-btn sid-btn-light sid-btn__try-again"
      onClick={onClickHandler}
      onKeyPress={onClickHandler}
    >
      <Message id="tryAgain" />
    </button>
  );
};

const RedirectButton = ({ redirectUrl, target = "_blank" }) => (
  <LinkExternal target={target} href={redirectUrl.toString()} className="sid-btn sid-btn--dark">
    <Message id="seeOtherOffers" />
  </LinkExternal>
);

type AssertValidTryAgainAction = (action: unknown) => asserts action is TryAgainAction;
const assertValidTryAgainAction: AssertValidTryAgainAction = (action: unknown) => {
  if (
    !(
      typeof action === "object" &&
      "type" in action &&
      Object.values(TryAgainActionType).includes(
        action.type as (typeof TryAgainActionType)[keyof typeof TryAgainActionType],
      )
    )
  )
    throw new RangeError("Invalid tryAgainAction option type - using default TryAgainButton.");

  return undefined;
};

export const TryAgainActionComponent = () => {
  const action = getOption("tryAgainAction");
  try {
    assertValidTryAgainAction(action);

    switch (action.type) {
      case TryAgainActionType.NONE:
        return null;
      case TryAgainActionType.POSTMESSAGE:
        if (window.self === window.top) {
          logger.error(
            `Invalid tryAgainAction option type "${action.type}" - page must be embedded in an iframe.`,
          );
          return null;
        }
        return <PostMessageButton />;
      case TryAgainActionType.REDIRECT:
        return <RedirectButton redirectUrl={action.url} target={action.target} />;
      case TryAgainActionType.RESET:
        return <TryAgainButtonComponent />;
      default:
        throw new RangeError("Invalid tryAgainAction option type - using default TryAgainButton.");
    }
  } catch (e: any) {
    logger.error(e.message);
    return <TryAgainButtonComponent />;
  }
};

export type TryAgainNoneAction = { type: typeof TryAgainActionType.NONE };
export type TryAgainResetAction = { type: typeof TryAgainActionType.RESET };
export type TryAgainPostMessageAction = { type: typeof TryAgainActionType.POSTMESSAGE };
export type TryAgainRedirectAction = {
  type: typeof TryAgainActionType.REDIRECT;
  target?: string;
  url: URL;
};

export type TryAgainAction =
  | TryAgainNoneAction
  | TryAgainResetAction
  | TryAgainPostMessageAction
  | TryAgainRedirectAction;
